import React, { useState, useEffect } from "react";
import PageHeader1 from "../../components/common/PageHeader1";
import { useNavigate } from "react-router-dom";
import { getDashboardData } from "../../services/Dashboard";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from "recharts";
import { FaAngleDown } from "react-icons/fa";
import "./Dashboard.scss";

function Dashboard() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true); 
  const [dashboard, setDashboard] = useState({
    totalUsers: 0,
    totalBlogs: 0,
    totalMeditationSeries: 0,
    totalResources: 0,
    monthlySignups: 0,
    activeUsers: {
      daily: 0,
      weekly: 0,
      monthly: 0,
    },
    growth:{
      daily:'0.00% from yesterday',
      weekly:'0.00% from last week',
      monthly:'0.00% from last month',
      signups:'0.00% signup growth from last month'
    },
    totals:{
      totalUsers:0,
    },
    sessionStats: { meditation: 10, sleepStories: 15, journaling: 5 },
  });

  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [selectedSignupsPeriod, setSelectedSignupsPeriod] = useState('monthly');
  const [isSignupsDropdownOpen, setIsSignupsDropdownOpen] = useState(false);
  const [isAnalyticsDropdownOpen, setIsAnalyticsDropdownOpen] = useState(false);
 
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getDashboardData();
        if (res.success) {
          setDashboard(res.data);
        }
      } catch (error) {
        console.error("Dashboard API Error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSignupsDropdownOpen && !event.target.closest('.analytics-dropdown')) {
        setIsSignupsDropdownOpen(false);
      }
      if (isAnalyticsDropdownOpen && !event.target.closest('.analytics-overview-dropdown')) {
        setIsAnalyticsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSignupsDropdownOpen, isAnalyticsDropdownOpen]);

  
  const sessionData = [
    { key: "Meditation Completed", name: "Meditation", color: "#00BFFF" },
    { key: "Sleep Story Completed", name: "Sleep Stories", color: "#6C5CE7" },
    { key: "Journal Entry Created", name: "Journaling", color: "#00D2D3" },
  ].map((item) => ({
    name: item.name,
    Completed: dashboard.sessionCounts?.[item.key] || 0,
    color: item.color,
  }));

  const moodTrendsData = [
    { day: "Mon", mood: 7, stress: 3, energy: 6 },
    { day: "Tue", mood: 8, stress: 2, energy: 7 },
    { day: "Wed", mood: 7, stress: 3, energy: 6 },
    { day: "Thu", mood: 8, stress: 2, energy: 7 },
    { day: "Fri", mood: 8, stress: 1, energy: 8 },
    { day: "Sat", mood: 9, stress: 1, energy: 8 },
    { day: "Sun", mood: 8, stress: 2, energy: 7 },
  ];

  const popularContent = [
    { title: "Morning Meditation", type: "Meditation", plays: 1250, rating: 4.8 },
    { title: "Ocean Waves", type: "Sleep Story", plays: 980, rating: 4.9 },
    { title: "Stress Relief", type: "Meditation", plays: 850, rating: 4.7 },
    { title: "Forest Sounds", type: "Sleep Story", plays: 720, rating: 4.6 },
  ];

  const communityActivity = [
    { user: "Sarah M.", action: "posted a new meditation tip", time: "2 hours ago", likes: 12 },
    { user: "Mike R.", action: "shared their wellness journey", time: "4 hours ago", likes: 8 },
    { user: "Emma L.", action: "commented on sleep story", time: "6 hours ago", likes: 5 },
    { user: "David K.", action: "started a new discussion", time: "8 hours ago", likes: 15 },
  ];

  const leaderboard = [
    { rank: 1, name: "Sarah M.", points: 2450, streak: 28 },
    { rank: 2, name: "Mike R.", points: 2180, streak: 22 },
    { rank: 3, name: "Emma L.", points: 1950, streak: 19 },
    { rank: 4, name: "David K.", points: 1820, streak: 16 },
    { rank: 5, name: "Lisa P.", points: 1650, streak: 14 },
  ];

  const transformMoodTrendData = (moodTrend) => {
    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const transformedData = days.map((day) => {
      const dayData = { day };
      moodTrend.forEach((mood) => {
        const moodDayData = mood.data.find((d) => d.day === day);
        dayData[mood.mood] = moodDayData ? moodDayData.count : 0;
      });
      return dayData;
    });
    return transformedData;
  };

  const trendingTags = [
    { tag: "#meditation", posts: 45, trend: "up" },
    { tag: "#sleep", posts: 32, trend: "up" },
    { tag: "#wellness", posts: 28, trend: "up" },
    { tag: "#mindfulness", posts: 22, trend: "down" },
    { tag: "#stressrelief", posts: 18, trend: "up" },
  ];

  const topForumPosts = [
    { title: "Best meditation techniques for beginners", author: "Sarah M.", replies: 24, views: 156 },
    { title: "How to improve sleep quality naturally", author: "Mike R.", replies: 18, views: 134 },
    { title: "Managing anxiety through mindfulness", author: "Emma L.", replies: 15, views: 98 },
    { title: "Creating a morning wellness routine", author: "David K.", replies: 12, views: 87 },
  ];

  // Data for different time periods
  const getAnalyticsData = (period) => {
    switch (period) {
      case 'weekly':
        return [
          { name: 'Mon', users: 45, sessions: 120, revenue: 850 },
          { name: 'Tue', users: 52, sessions: 135, revenue: 920 },
          { name: 'Wed', users: 48, sessions: 128, revenue: 880 },
          { name: 'Thu', users: 61, sessions: 145, revenue: 1050 },
          { name: 'Fri', users: 55, sessions: 138, revenue: 950 },
          { name: 'Sat', users: 38, sessions: 95, revenue: 720 },
          { name: 'Sun', users: 42, sessions: 108, revenue: 780 }
        ];
      case 'yearly':
        return [
          { name: 'Jan', users: 1200, sessions: 3200, revenue: 18500 },
          { name: 'Feb', users: 1350, sessions: 3600, revenue: 21000 },
          { name: 'Mar', users: 1420, sessions: 3800, revenue: 22500 },
          { name: 'Apr', users: 1380, sessions: 3700, revenue: 21800 },
          { name: 'May', users: 1500, sessions: 4000, revenue: 23500 },
          { name: 'Jun', users: 1450, sessions: 3900, revenue: 22800 },
          { name: 'Jul', users: 1600, sessions: 4200, revenue: 24500 },
          { name: 'Aug', users: 1550, sessions: 4100, revenue: 23800 },
          { name: 'Sep', users: 1480, sessions: 3950, revenue: 23200 },
          { name: 'Oct', users: 1620, sessions: 4300, revenue: 25200 },
          { name: 'Nov', users: 1580, sessions: 4200, revenue: 24800 },
          { name: 'Dec', users: 1700, sessions: 4500, revenue: 26500 }
        ];
      default: // monthly
        return [
          { name: 'Week 1', users: 180, sessions: 450, revenue: 3200 },
          { name: 'Week 2', users: 195, sessions: 480, revenue: 3450 },
          { name: 'Week 3', users: 210, sessions: 520, revenue: 3700 },
          { name: 'Week 4', users: 225, sessions: 560, revenue: 3950 }
        ];
    }
  };

  
  

  const transformLeaderboardData = (leaderboardResponse) => {
    return leaderboardResponse.map((entry) => ({
      rank: entry.rank,
      name: `${entry.user.first_name || "Anonymous"} ${entry.user.last_name || ""}`.trim(),
      email: entry.user.email || "N/A",
      points: entry.weeklyPoints,
      category: entry.category,
      minutes: entry.minutes,
    }));
  };

  const getSignupsData = (period) => {
    if (!dashboard.signupStats) return [];
  
    switch (period) {
      case "weekly":
        return dashboard.signupStats.weekly.labels.map((label, idx) => ({
          name: label,
          signups: dashboard.signupStats.weekly.actualSignups[idx] || 0,
        }));
  
      case "monthly":
        return dashboard.signupStats.monthly.labels.map((label, idx) => ({
          name: label,
          signups: dashboard.signupStats.monthly.actualSignups[idx] || 0,
        }));
  
      case "yearly":
        return dashboard.signupStats.yearly.map((monthData) => ({
          name: monthData.month,
          signups: monthData.totalSignups || 0,
        }));
  
      default:
        return [];
    }
  };


  const getMixpanelEventData = (period) => {
    console.log(period);
    if (!dashboard.mixpanelEventTrend) return [];
    console.log(444);
    const periodData = dashboard.mixpanelEventTrend[period];
    if (!periodData) return [];
  
    const transformedData = [];
    const labels = period === 'weekly' ? ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
      : period === 'monthly' ? ['Week 1', 'Week 2', 'Week 3', 'Week 4']
      : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
    labels.forEach((label, index) => {
      const dataPoint = { name: label };
      periodData.forEach((event) => {
        const eventData = event.data[index];
        dataPoint[event.event] = eventData ? eventData.count : 0;
      });
      transformedData.push(dataPoint);
    });
  
    return transformedData;
  };
  // const getSignupsData = (period) => {
  //   if (!dashboard.signupStats) return [];
  
  //   switch (period) {
  //     case "weekly":
  //       return dashboard.signupStats.weekly.labels.map((label, idx) => ({
  //         name: label,
  //         signups: dashboard.signupStats.weekly.actualSignups[idx] || 0,
  //       }));
  
  //     case "monthly":
  //       return dashboard.signupStats.monthly.map((m) => ({
  //         name: m.month,
  //         totalSignups: m.totalSignups || 0,
  //         weeklySignups: m.weeklySignups || [0, 0, 0, 0],
  //       }));
  
  //     case "yearly":
  //       return dashboard.signupStats.yearly.labels.map((label, idx) => ({
  //         name: label,
  //         signups: dashboard.signupStats.yearly.actualSignups[idx] || 0,
  //       }));
  
  //     default:
  //       return [];
  //   }
  // };
  // const COLORS = ['#00BFFF', '#6C5CE7', '#00D2D3', '#FF6B6B', '#4ECDC4'];
  const COLORS = ["#00BFFF", "#FF6B6B", "#28a745", "#6C5CE7", "#FFC107", "#4ECDC4", "#FF5733"];
  if (isLoading) {
  return (
    <div className="body d-flex py-lg-3 py-md-2">
      <div className="container-xxl">
          <PageHeader1 pagetitle="Dashboard" />
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading Dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="body d-flex py-lg-3 py-md-2">
      <div className="container-xxl">
        <PageHeader1 pagetitle="Dashboard" />

        <div className="row g-4 mt-0">
          {/* User Metrics Cards - Enhanced Layout */}
          <div className="col-12">
            <div className="row g-4">
                {/* Total Users Card */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
                <div className="card modern-card metric-card h-100">
                  <div className="card-body">
                    <div className="metric-content">
                      <div className="metric-icon bg-primary-light">
                        <i className="icofont-users-alt-3"></i>
                      </div>
                      <div className="metric-info">
                        <div className="metric-label">Total Users</div>
                        <div className="metric-value">{dashboard.totals.totalUsers}</div>
                        <div className="metric-change positive">
                          {/* <i className="icofont-arrow-up"></i> */}
                          {/* <span>+12% from last month</span> */}
                          {/* <span>+{dashboard.growth.daily}</span> */}
                          {/* <span>+{dashboard.growth.signups}</span> */}
                        </div>
                      </div>
                      </div>
                    </div>
                  </div>
                </div>

              {/* Daily Active Users */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
                <div className="card modern-card metric-card h-100">
                  <div className="card-body">
                    <div className="metric-content">
                      <div className="metric-icon bg-success-light">
                        <i className="icofont-chart-line"></i>
                      </div>
                      <div className="metric-info">
                        <div className="metric-label">Daily Active</div>
                        <div className="metric-value">{dashboard.activeUsers.daily}</div>
                        <div className="metric-change positive">
                          {/* <i className="icofont-arrow-up"></i> */}
                          {/* <span>+8% from yesterday</span> */}
                          {/* <span>+{dashboard.growth.daily}</span> */}
                        </div>
                      </div>
                      </div>
                    </div>
                  </div>
                </div>

              {/* Weekly Active Users */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
                <div className="card modern-card metric-card h-100">
                  <div className="card-body">
                    <div className="metric-content">
                      <div className="metric-icon bg-warning-light">
                        <i className="icofont-user" style={{ fontSize: '1.5rem', color: 'white' }}></i>
                      </div>
                      <div className="metric-info">
                        <div className="metric-label">Weekly Active</div>
                        <div className="metric-value">{dashboard.activeUsers.weekly}</div>
                        <div className="metric-change positive">
                          {/* <i className="icofont-arrow-up"></i> */}
                          {/* <span>+15% from last week</span> */}
                          {/* <span>+{dashboard.growth.weekly}</span> */}
                        </div>
                      </div>
                      </div>
                    </div>
                  </div>
                </div>

              {/* Monthly Active Users */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
                <div className="card modern-card metric-card h-100">
                  <div className="card-body">
                    <div className="metric-content">
                      <div className="metric-icon bg-info-light">
                        <i className="icofont-chart-pie"></i>
                      </div>
                      <div className="metric-info">
                        <div className="metric-label">Monthly Active</div>
                        <div className="metric-value">{dashboard.activeUsers.monthly}</div>
                        <div className="metric-change positive">
                          {/* <i className="icofont-arrow-up"></i> */}
                          {/* <span>+22% from last month</span> */}
                          {/* <span>+{dashboard.growth.monthly}</span> */}
                        </div>
                      </div>
                    </div>
                      </div>
                      </div>
                    </div>
                  </div>
                </div>

          {/* New Sign-ups Chart */}
          <div className="col-lg-6 col-md-6">
          <div className="card modern-card h-100">
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h5 className="mb-0 fw-bold text-dark">New Sign-ups</h5>
                <div className="dropdown analytics-dropdown" style={{ position: "relative" }}>
                  <button
                    className="btn"
                    type="button"
                    onClick={() => setIsSignupsDropdownOpen(!isSignupsDropdownOpen)}
                    style={{
                      border: "1px solid #e0e0e0",
                      color: "#333",
                      background: "#fff",
                      borderRadius: "12px",
                      padding: "10px 20px",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ background: "none", boxShadow: "none" }}>
                      {selectedSignupsPeriod === "weekly"
                        ? "Weekly"
                        : selectedSignupsPeriod === "yearly"
                        ? "Yearly"
                        : "Monthly"}
                    </span>
                    <FaAngleDown
                      style={{
                        fontSize: "16px",
                        transition: "transform 0.3s ease",
                        transform: isSignupsDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                        background: "none",
                        boxShadow: "none",
                      }}
                    />
                  </button>
                  {isSignupsDropdownOpen && (
                    <ul
                      className="dropdown-menu"
                      style={{
                        position: "absolute",
                        top: "100%",
                        right: "0",
                        zIndex: 1000,
                        display: "block",
                        minWidth: "120px",
                        padding: "8px 0",
                        margin: "8px 0 0",
                        backgroundColor: "#fff",
                        border: "none",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    >
                      <li>
                        <button
                          className={`dropdown-item ${selectedSignupsPeriod === "weekly" ? "active" : ""}`}
                          onClick={() => {
                            setSelectedSignupsPeriod("weekly");
                            setIsSignupsDropdownOpen(false);
                          }}
                          style={{
                            display: "block",
                            width: "100%",
                            padding: "8px 16px",
                            fontSize: "14px",
                            color: selectedSignupsPeriod === "weekly" ? "#fff" : "#6c757d",
                            backgroundColor: selectedSignupsPeriod === "weekly" ? "#00BFFF" : "transparent",
                            border: "none",
                            textAlign: "left",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                        >
                          📅 Weekly
                        </button>
                      </li>
                      <li>
                        <button
                          className={`dropdown-item ${selectedSignupsPeriod === "monthly" ? "active" : ""}`}
                          onClick={() => {
                            setSelectedSignupsPeriod("monthly");
                            setIsSignupsDropdownOpen(false);
                          }}
                          style={{
                            display: "block",
                            width: "100%",
                            padding: "8px 16px",
                            fontSize: "14px",
                            color: selectedSignupsPeriod === "monthly" ? "#fff" : "#6c757d",
                            backgroundColor: selectedSignupsPeriod === "monthly" ? "#00BFFF" : "transparent",
                            border: "none",
                            textAlign: "left",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                        >
                          📊 Monthly
                        </button>
                      </li>
                      <li>
                        <button
                          className={`dropdown-item ${selectedSignupsPeriod === "yearly" ? "active" : ""}`}
                          onClick={() => {
                            setSelectedSignupsPeriod("yearly");
                            setIsSignupsDropdownOpen(false);
                          }}
                          style={{
                            display: "block",
                            width: "100%",
                            padding: "8px 16px",
                            fontSize: "14px",
                            color: selectedSignupsPeriod === "yearly" ? "#fff" : "#6c757d",
                            backgroundColor: selectedSignupsPeriod === "yearly" ? "#00BFFF" : "transparent",
                            border: "none",
                            textAlign: "left",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                        >
                          📈 Yearly
                        </button>
                      </li>
                    </ul>
                  )}
                </div>
              </div>

              <div className="chart-container" style={{ height: "300px" }}>
                <ResponsiveContainer width="100%" height="100%" key={selectedSignupsPeriod}>
                  <BarChart
                    data={getSignupsData(selectedSignupsPeriod)}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="name"
                      stroke="#6c757d"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "#6c757d" }}
                    />
                    <YAxis
                      stroke="#6c757d"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "#6c757d" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "none",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        fontSize: "14px",
                      }}
                      labelStyle={{ color: "#333", fontWeight: "bold" }}
                    />
                    <Legend wrapperStyle={{ paddingTop: "20px" }} iconType="rect" />
                    <Bar
                      dataKey="signups"
                      fill="#00BFFF"
                      radius={[4, 4, 0, 0]}
                      name="Actual Sign-ups"
                      animationDuration={800}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

          {/* Sessions Played Chart */}
          <div className="col-lg-6">
            <div className="card modern-card h-100">
              <div className="card-body p-4">
                <h5 className="mb-4 fw-bold text-dark">Sessions Played</h5>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={sessionData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 13, fill: '#6c757d' }}
                        axisLine={{ stroke: '#e9ecef' }}
                      />
                      <YAxis 
                        allowDecimals={false} 
                        tick={{ fontSize: 13, fill: '#6c757d' }}
                        axisLine={{ stroke: '#e9ecef' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e9ecef', 
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          fontSize: '14px'
                        }} 
                      />
                      <Bar dataKey="Completed" radius={[6, 6, 0, 0]} fill="#00BFFF">
                        <LabelList dataKey="Completed" position="top" style={{ fontSize: '13px', fill: '#495057' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
                      </div>

          {/* Mood Trends Graph */}
          <div className="col-12">
  <div className="card modern-card">
    <div className="card-body p-4">
      <h5 className="mb-4 fw-bold text-dark">Mood Trends (7 Days)</h5>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart
            data={transformMoodTrendData(dashboard.moodTrend)}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <defs>
              {dashboard.moodTrend?.map((moodData, index) => (
                <linearGradient
                  key={index}
                  id={`color${moodData.mood}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={COLORS[index % COLORS.length]}
                    stopOpacity={0.4}
                  />
                  <stop
                    offset="95%"
                    stopColor={COLORS[index % COLORS.length]}
                    stopOpacity={0.1}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="day"
              tick={{ fontSize: 13, fill: "#6c757d" }}
              axisLine={{ stroke: "#e9ecef" }}
            />
            <YAxis
              domain={[0, 10]}
              tick={{ fontSize: 13, fill: "#6c757d" }}
              axisLine={{ stroke: "#e9ecef" }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "#fff",
                border: "1px solid #e9ecef",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                fontSize: "14px",
              }}
            />
            <Legend />
            {dashboard.moodTrend?.map((moodData, index) => (
              <Area
                key={index}
                type="monotone"
                dataKey={moodData.mood}
                name={moodData.mood}
                stroke={COLORS[index % COLORS.length]}
                strokeWidth={2}
                fillOpacity={1}
                fill={`url(#color${moodData.mood})`}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  </div>
</div>

          {/* Popular Content */}
          <div className="col-lg-6">
            <div className="card modern-card h-100">
              <div className="card-body p-4">
                <h5 className="mb-4 fw-bold text-dark">Popular Content</h5>
                <div className="content-scroll">
                  {popularContent.map((content, index) => (
                    <div key={index} className="d-flex align-items-center p-3 bg-light rounded-3 mb-3">
                      <div className="content-icon me-3">
                        <i className={`icofont-${content.type === 'Meditation' ? 'music-note' : 'moon'} fs-4 text-white`}></i>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mb-1 fw-semibold text-dark">{content.title}</h6>
                        <small className="text-muted">{content.type} • {content.plays} plays</small>
                      </div>
                      <div className="text-end">
                        <div className="rating">
                          <i className="icofont-star text-warning"></i>
                          <span className="ms-1 fw-semibold">{content.rating}</span>
                        </div>
                      </div>
                      </div>
                  ))}
                      </div>
                    </div>
                  </div>
                </div>

          {/* Community Activity */}
          <div className="col-lg-6">
  <div className="card modern-card h-100">
    <div className="card-body p-4">
      <h5 className="mb-4 fw-bold text-dark">Recent Community Activity</h5>
      <div className="content-scroll">
        {dashboard.communityActivity.map((activity, index) => {
          // Calculate time difference
          const timeDifference = (createdAt) => {
            const now = new Date();
            const activityTime = new Date(createdAt);
            const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60));
            if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
            const diffInHours = Math.floor(diffInMinutes / 60);
            if (diffInHours < 24) return `${diffInHours} hours ago`;
            const diffInDays = Math.floor(diffInHours / 24);
            return `${diffInDays} days ago`;
          };

          // Determine the activity format
          let activityBoldText = "";
          let activityText = "";
          if (activity.activityType === "Comment") {
            activityBoldText = `${activity.userName}`;
            activityText = ` commented on ${activity.postOwnerId?.first_name || "someone"}'s post`;
          } else if (activity.activityType === "Post") {
            if (activity.title) {
              activityBoldText = `${activity.userName} `;
              activityText = `posted ${activity.title}`;
            } else {
              activityBoldText = `${activity.userName} `;
              activityText = `posted a media`;
            }
          }

          return (
            <div key={index} className="d-flex align-items-start p-3 bg-light rounded-3 mb-3">
              <div className="avatar-sm me-3">
                <div className="avatar-title bg-primary-light text-white rounded-circle">
                  {/* {activity.userName.charAt(0)} */}
                </div>
              </div>
              <div className="flex-grow-1">
                <p className="mb-1">
                  <strong className="text-dark">{activityBoldText}</strong>{activityText}
                </p>
                <small className="text-muted">
                  {timeDifference(activity.createdAt)} • {activity.likeCount || 0} likes
                </small>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
</div>

          {/* Analytics Graph with Dropdown */}
          <div className="col-12">
  <div className="card modern-card">
    <div className="card-body p-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h5 className="mb-0 fw-bold text-dark">Analytics Overview</h5>
        <div className="dropdown analytics-overview-dropdown" style={{ position: 'relative' }}>
          <button
            className="btn"
            type="button"
            onClick={() => setIsAnalyticsDropdownOpen(!isAnalyticsDropdownOpen)}
            style={{
              border: '1px solid #e0e0e0',
              color: '#333',
              background: '#fff',
              borderRadius: '12px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#00BFFF';
              e.target.style.color = '#00BFFF';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 191, 255, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#e0e0e0';
              e.target.style.color = '#333';
              e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }}
          >
            <span style={{ background: 'none', boxShadow: 'none' }}>
              {selectedPeriod === 'weekly' ? 'Weekly' : selectedPeriod === 'yearly' ? 'Yearly' : 'Monthly'}
            </span>
            <FaAngleDown
              style={{
                fontSize: '16px',
                transition: 'transform 0.3s ease',
                transform: isAnalyticsDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                background: 'none',
                boxShadow: 'none',
              }}
            />
          </button>
          {isAnalyticsDropdownOpen && (
            <ul
              className="dropdown-menu"
              style={{
                position: 'absolute',
                top: '100%',
                right: '0',
                zIndex: 1000,
                display: 'block',
                minWidth: '120px',
                padding: '8px 0',
                margin: '8px 0 0',
                backgroundColor: '#fff',
                border: 'none',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              <li>
                <button
                  className={`dropdown-item ${selectedPeriod === 'weekly' ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedPeriod('weekly');
                    setIsAnalyticsDropdownOpen(false);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: selectedPeriod === 'weekly' ? '#fff' : '#6c757d',
                    backgroundColor: selectedPeriod === 'weekly' ? '#00BFFF' : 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  📅 Weekly
                </button>
              </li>
              <li>
                <button
                  className={`dropdown-item ${selectedPeriod === 'monthly' ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedPeriod('monthly');
                    setIsAnalyticsDropdownOpen(false);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: selectedPeriod === 'monthly' ? '#fff' : '#6c757d',
                    backgroundColor: selectedPeriod === 'monthly' ? '#00BFFF' : 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  📊 Monthly
                </button>
              </li>
              <li>
                <button
                  className={`dropdown-item ${selectedPeriod === 'yearly' ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedPeriod('yearly');
                    setIsAnalyticsDropdownOpen(false);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: selectedPeriod === 'yearly' ? '#fff' : '#6c757d',
                    backgroundColor: selectedPeriod === 'yearly' ? '#00BFFF' : 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  📈 Yearly
                </button>
              </li>
            </ul>
          )}
        </div>
      </div>

      <div className="chart-container" style={{ height: '400px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={getMixpanelEventData(selectedPeriod)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" stroke="#6c757d" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis stroke="#6c757d" fontSize={12} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: 'none',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '14px',
              }}
            />
            <Legend />
            {['Meditation Started', 'Sleep Story Played', 'Journal Entry Created'].map((event, index) => (
              <Line
                key={event}
                type="monotone"
                dataKey={event}
                stroke={COLORS[index % COLORS.length]}
                strokeWidth={3}
                dot={{ fill: COLORS[index % COLORS.length], strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: COLORS[index % COLORS.length], strokeWidth: 2 }}
                name={event}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  </div>
</div>

          {/* Leaderboard */}
          <div className="col-lg-4">
            <div className="card modern-card h-100">
              <div className="card-body p-4">
                <h5 className="mb-4 fw-bold text-dark">Leaderboard</h5>
                <div className="content-scroll">
                  {transformLeaderboardData(dashboard.Leaderboard?.leaderboard || []).map((user, index) => (
                    <div key={index} className="d-flex align-items-center p-3 bg-light rounded-3 mb-3">
                      <div className={`rank-badge me-3 ${index < 3 ? 'top-rank' : ''}`}>
                        {user.rank}
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mb-1 fw-semibold text-dark">{user.name}</h6>
                        <small className="text-muted">
                          {user.points} points 
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Trending Discussions */}
          {/* <div className="col-lg-4">
            <div className="card modern-card h-100">
              <div className="card-body p-4">
                <h5 className="mb-4 fw-bold text-dark">Trending Tags</h5>
                <div className="content-scroll">
                  {trendingTags.map((tag, index) => (
                    <div key={index} className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 mb-3">
                      <div>
                        <h6 className="mb-1 fw-semibold text-dark">{tag.tag}</h6>
                        <small className="text-muted">{tag.posts} posts</small>
                      </div>
                      <div className={`trend-indicator ${tag.trend === 'up' ? 'trend-up' : 'trend-down'}`}>
                        <i className={`icofont-arrow-${tag.trend}`}></i>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
      </div> */}

          {/* Top Forum Posts */}
          {/* <div className="col-lg-4">
            <div className="card modern-card h-100">
              <div className="card-body p-4">
                <h5 className="mb-4 fw-bold text-dark">Top Forum Posts</h5>
                <div className="content-scroll">
                  {topForumPosts.map((post, index) => (
                    <div key={index} className="p-3 bg-light rounded-3 mb-3">
                      <h6 className="mb-2 fw-semibold text-dark">{post.title}</h6>
                      <small className="text-muted d-block mb-2">by {post.author}</small>
                      <div className="d-flex justify-content-between">
                        <small className="text-muted">{post.replies} replies</small>
                        <small className="text-muted">{post.views} views</small>
                      </div>
                  </div>
                  ))}
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </div>
     
    </div>
  );
}
export default Dashboard;